import { AxiosError } from 'axios'

let recaptchaScriptPromise: Promise<void> | null = null
let recaptchaScriptSiteKey: string | null = null
const recaptchaLoadTimeoutMs = 10000

function recaptchaApiIsReady(): boolean {
    return typeof window.grecaptcha?.ready === 'function' &&
        typeof window.grecaptcha?.execute === 'function' &&
        typeof window.grecaptcha?.render === 'function' &&
        typeof window.grecaptcha?.reset === 'function'
}

function waitForRecaptchaApi(): Promise<void> {
    const deadline = Date.now() + recaptchaLoadTimeoutMs
    return new Promise((resolve, reject) => {
        const check = () => {
            if(recaptchaApiIsReady()) {
                resolve()
            } else if(Date.now() >= deadline) {
                reject(new Error('reCAPTCHA did not finish loading'))
            } else {
                window.setTimeout(check, 50)
            }
        }
        check()
    })
}

export function loadRecaptchaScript(recaptchaV3SiteKey: string): Promise<void> {
    if(recaptchaApiIsReady()) {
        return Promise.resolve()
    }

    if(!recaptchaScriptPromise || recaptchaScriptSiteKey !== recaptchaV3SiteKey) {
        recaptchaScriptSiteKey = recaptchaV3SiteKey
        const existingScript = document.getElementById('google-recaptcha-script') as HTMLScriptElement | null
        const scriptLoaded = existingScript
            ? Promise.resolve()
            : new Promise<void>((resolve, reject) => {
                const script = document.createElement('script')
                script.id = 'google-recaptcha-script'
                script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(recaptchaV3SiteKey)}`
                script.async = true
                script.defer = true
                script.onload = () => resolve()
                script.onerror = () => reject(new Error('Could not load reCAPTCHA'))
                document.head.appendChild(script)
            })
        recaptchaScriptPromise = scriptLoaded
            .then(() => waitForRecaptchaApi())
            .catch(error => {
                recaptchaScriptPromise = null
                throw error
            })
    }

    return recaptchaScriptPromise
}

export function executeRecaptchaV3(siteKey: string, action: string): Promise<string> {
    return new Promise((resolve, reject) => {
        if(!recaptchaApiIsReady() || !siteKey) {
            reject(new Error('reCAPTCHA is not ready'))
            return
        }
        window.grecaptcha.ready(async () => {
            try {
                resolve(await window.grecaptcha!.execute(siteKey, { action }))
            } catch(error) {
                reject(error)
            }
        })
    })
}

export function describeRecaptchaLoadError(error: unknown): string {
    if(error instanceof Error && error.message) {
        return error.message
    }

    const axiosError = error as AxiosError<{ message?: string }> | undefined
    const status = axiosError?.response?.status
    const responseMessage = axiosError?.response?.data?.message
    const requestUrl = axiosError?.config?.url
    if(status && requestUrl) {
        return `Config request failed (${status}) for ${requestUrl}${responseMessage ? `: ${responseMessage}` : ''}`
    }
    return status ? `Config request failed (${status})${responseMessage ? `: ${responseMessage}` : ''}` : 'Unknown reCAPTCHA initialization error'
}
